export interface StarfieldPoint {
  x: number
  y: number
  tier: 'near' | 'mid' | 'far'
  gold: boolean
  alt: boolean
  delay: number
  className: string
}

export function capDpr(dpr: number): number {
  return Math.min(dpr || 1, 2)
}

export function shouldDrawFrame(t: number, last: number, frameMs: number): boolean {
  if (!frameMs || frameMs <= 0) return true
  return t - last >= frameMs
}

export function stepDots(
  dots: Array<{ x: number; y: number; vx: number; vy: number }>,
  w: number,
  h: number,
  step: number = 1,
): void {
  for (const d of dots) {
    d.x += d.vx * step
    d.y += d.vy * step
    if (d.x < 0 || d.x > w) d.vx *= -1
    if (d.y < 0 || d.y > h) d.vy *= -1
  }
}

export function cappedLinks(
  dots: Array<{ x: number; y: number }>,
  link: number = 140,
  cap: number = 3,
): Array<[number, number, number]> {
  const link2 = link * link
  const linksCount = new Array<number>(dots.length).fill(0)
  const grid = new Map<string, number[]>()

  for (let i = 0; i < dots.length; i++) {
    const cx = Math.floor(dots[i].x / link)
    const cy = Math.floor(dots[i].y / link)
    const key = `${cx},${cy}`
    let cell = grid.get(key)
    if (!cell) {
      cell = []
      grid.set(key, cell)
    }
    cell.push(i)
  }

  const result: Array<[number, number, number]> = []

  for (let i = 0; i < dots.length; i++) {
    if (linksCount[i] >= cap) continue
    const cx = Math.floor(dots[i].x / link)
    const cy = Math.floor(dots[i].y / link)

    for (let oy = -1; oy <= 1 && linksCount[i] < cap; oy++) {
      for (let ox = -1; ox <= 1 && linksCount[i] < cap; ox++) {
        const cell = grid.get(`${cx + ox},${cy + oy}`)
        if (!cell) continue

        for (const j of cell) {
          if (j <= i || linksCount[i] >= cap || linksCount[j] >= cap) continue
          const dx = dots[i].x - dots[j].x
          const dy = dots[i].y - dots[j].y
          const d2 = dx * dx + dy * dy
          if (d2 < link2) {
            const alpha = 1 - d2 / link2
            linksCount[i]++
            linksCount[j]++
            result.push([i, j, alpha])
          }
        }
      }
    }
  }

  return result
}

export function bucketedLinks(
  dots: Array<{ x: number; y: number }>,
  link: number = 90,
  buckets: number = 6,
): number[][] {
  const link2 = link * link
  const result: number[][] = Array.from({ length: buckets }, () => [])

  for (let i = 0; i < dots.length; i++) {
    const a = dots[i]
    for (let j = i + 1; j < dots.length; j++) {
      const b = dots[j]
      const dx = a.x - b.x
      if (dx > link || dx < -link) continue
      const dy = a.y - b.y
      if (dy > link || dy < -link) continue
      const d2 = dx * dx + dy * dy
      if (d2 < link2) {
        const bucketIdx = Math.min(buckets - 1, Math.floor((1 - d2 / link2) * buckets))
        result[bucketIdx].push(a.x, a.y, b.x, b.y)
      }
    }
  }

  return result
}

const SECTION_RAW_PTS: Array<[number, number]> = [
  [20, 70],
  [50, 30],
  [80, 62],
  [112, 22],
  [140, 58],
  [176, 34],
  [160, 90],
  [104, 92],
  [58, 96],
]

const FAR_RAW_PTS: Array<[number, number]> = [
  [8, 18],
  [34, 8],
  [92, 12],
  [70, 82],
  [28, 56],
  [96, 70],
]

export function sectionStarfieldPoints(): StarfieldPoint[] {
  const points: StarfieldPoint[] = []

  // 9 constellation points
  for (let i = 0; i < SECTION_RAW_PTS.length; i++) {
    const [rx, ry] = SECTION_RAW_PTS[i]
    const x = (rx / 196) * 100
    const y = (ry / 116) * 100
    const tier: 'near' | 'mid' | 'far' = i % 3 === 0 ? 'near' : 'mid'
    const gold = i === 4
    const alt = !gold && i % 5 === 2
    const delay = +((i * 0.53) % 3.2).toFixed(2)
    const classes = ['dsLoader__dot']
    if (tier !== 'mid') {
      classes.push(`dsLoader__dot--${tier}`)
    }
    if (gold) {
      classes.push('dsLoader__dot--gold')
    } else if (alt) {
      classes.push('dsLoader__dot--alt')
    }
    points.push({
      x,
      y,
      tier,
      gold,
      alt,
      delay,
      className: classes.join(' '),
    })
  }

  // 6 far points
  for (let j = 0; j < FAR_RAW_PTS.length; j++) {
    const i = j + 9
    const [x, y] = FAR_RAW_PTS[j]
    const tier: 'near' | 'mid' | 'far' = 'far'
    const gold = i === 4
    const alt = !gold && i % 5 === 2
    const delay = +((i * 0.53) % 3.2).toFixed(2)
    const classes = ['dsLoader__dot', 'dsLoader__dot--far']
    if (gold) {
      classes.push('dsLoader__dot--gold')
    } else if (alt) {
      classes.push('dsLoader__dot--alt')
    }
    points.push({
      x,
      y,
      tier,
      gold,
      alt,
      delay,
      className: classes.join(' '),
    })
  }

  return points
}

export function fullscreenStarfieldPoints(): StarfieldPoint[] {
  let sd = 11
  const rnd = () => (sd = (sd * 9301 + 49297) % 233280) / 233280

  return Array.from({ length: 26 }, (_, i) => {
    const x = +(4 + rnd() * 92).toFixed(1)
    const y = +(6 + rnd() * 88).toFixed(1)
    const tier: 'near' | 'mid' | 'far' = i % 3 === 0 ? 'near' : i % 3 === 1 ? 'far' : 'mid'
    const gold = i % 7 === 3
    const alt = !gold && i % 5 === 2
    const delay = +((i * 0.53) % 3.2).toFixed(2)
    const classes = ['dsLoader__dot']
    if (tier !== 'mid') {
      classes.push(`dsLoader__dot--${tier}`)
    }
    if (gold) {
      classes.push('dsLoader__dot--gold')
    } else if (alt) {
      classes.push('dsLoader__dot--alt')
    }
    return {
      x,
      y,
      tier,
      gold,
      alt,
      delay,
      className: classes.join(' '),
    }
  })
}
